import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import i18n from 'simple-react-i18n'
import { compact, isNil, keys, orderBy, some, uniq, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import { H_HYDRO_DASHBOARD } from '../../../account/constants/AccessRulesConstants'
import MessageCard from '../../../components/card/MessageCard'
import ProgressCard from '../../../components/card/ProgressCard'
import CartographyPanel from '../../../components/map/CartographyPanel'
import StationStatisticPanel from '../../../station/components/dashboard/component/keyfigure/StationStatisticPanel'
import { MAP, STATION_LIST, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { getDate } from '../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { hasValue } from '../../../utils/NumberUtil'
import { getBookmarks, getStationType, hasLocalisationStation } from '../../../utils/StationUtils'
import { getLabel, getObjectLabel } from '../../../utils/StoreUtils'
import { getI18nTitleData, normalizeString } from '../../../utils/StringUtil'
import { getUserBookmarksByStationType } from '../../../utils/UserUtil'
import HydrometryAction from '../../actions/HydrometryAction'
import CityAction from '../../../referencial/components/city/actions/CityAction'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import { CardTitle } from '../../../components/card/NewCard'
import CampaignPortletPanel from 'campaign/components/CampaignPortletPanel'
import { getCampaignIcon, getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, TableProgressBar } from 'campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import { getColorCampaign } from 'utils/CampaignUtil'
import StationFilterFields from 'station/components/search/StationFilterFields'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import AddHydroStepper from '../addHydro/AddHydroStepper'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { Card, CardContent, Grid } from '@mui/material'
import Icon from 'components/icon/Icon'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { CardTable, sortRows } from 'components/datatable/NewTable'
import { FR, nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import useBoolean from 'utils/customHook/useBoolean'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import SimpleTabList from 'components/list/SimpleTabList'
import NetworkAction from 'referencial/components/network/actions/NetworkAction'
import { convertCoordinates, PROJECTIONS } from 'utils/mapUtils/CoordinateUtils'
import Select from 'components/forms/Select'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import { formatData } from 'utils/ExportDataUtil'
import CampaignAction from 'campaign/actions/CampaignAction'
import StationAction from 'station/actions/StationAction'
import moment from 'moment'

const CAMPAIGN_HEADERS = ['qualificationTableColor', 'nullValue', 'name', 'nbStations', 'startDate', 'progression', 'referent']
const STATION_HEADERS = ['code', 'internalReferences', 'city', 'name', 'creationDate', 'closeDate', 'stationType', 'administrator']

const ExportModal = ({
    isOpen,
    onClose = () => {},
    filteredHydrometers = [],
}) => {
    const dispatch = useDispatch()
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)

    const [projection, setProjection] = useState()

    const getLocalisation = useCallback(pluvio => {
        if (hasLocalisationStation(pluvio)) return { x: pluvio.x, y: pluvio.y, projection: pluvio.projection }

        const city = citiesIndex[pluvio.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])


    const onExport = (type) => {
        const pluviometersFormated = filteredHydrometers.map(pluvio => {
            const loc = getLocalisation(pluvio)
            const usedLoc = !isNil(projection) ? convertCoordinates(loc.projection, projection, loc) : loc

            return {
                ...pluvio,
                x: { value: usedLoc.x, format: '0.0000000000', cellType: 'number' },
                y: { value: usedLoc.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: projection ?? usedLoc.projection, format: '0', cellType: 'number' },
            }
        })

        if (pluviometersFormated.length) {
            const exportData = [
                {
                    ...pluviometersFormated[0],
                    headers: [...pluviometersFormated[0].headers, 'x', 'y', 'projection'],
                },
                ...pluviometersFormated.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.hydrometricStations))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={onClose}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                    settingsContent: (
                        <Select
                            label={i18n.coordinateConversion}
                            value={projection}
                            onChange={setProjection}
                            options={PROJECTIONS}
                            keyLabel='name'
                            keyValue='sandreCode'
                        />
                    ),
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filteredHydrometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const LegendPanel = () => (
    <Grid container spacing={1} style={{ padding: '4px' }} alignItems='center'>
        <Grid size='auto'>
            <Icon icon='wifi' />
        </Grid>
        <Grid size='auto'>
            {i18n.automaticUpdate}
        </Grid>
    </Grid>
)

const TablePanel = ({
    stations = [],
}) => {
    const DEFAULT_HEADERS = ['nullValue', 'nullValue2', 'code',
        { key: 'internalReferences', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.internalReferencesHeader}</span>) },
        'city', 'name', 'creationDate', 'closeDate', 'stationType', 'administrator']
    const dispatch = useDispatch()

    const {
        selectedSearchValues,
        filterResults,
    } = useSelector(store => ({
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        filterResults: store.StationReducer.filterResults,
    }), shallowEqual)

    const searchValues = selectedSearchValues.hydrometry
    const initialSort = searchValues?.columnName && { column: searchValues.columnName, sort: searchValues.sort }

    const filterHeaders = keys(filterResults?.[0] || {}).filter(d => d !== 'id')
    const headers = [...DEFAULT_HEADERS, ...filterHeaders]

    const onTableSort = sort => {
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE_NAME.hydrometry, { stations: sortRows(stations, sort), sort }))
    }

    const onSaveSearchValue = () => {
        dispatch(AdministrationAction.setSelectedSearchValues(STATION_TYPE_NAME.hydrometry, { stations: sortRows(stations, initialSort), initialSort }))
    }

    useEffect(() => {
        onSaveSearchValue(stations)
    }, [stations])

    return (
        <CardTable
            title={i18n.hydrometricStations}
            actions={[{
                icon: 'info_outlined',
                tooltip: <LegendPanel />,
            }]}

            rows={stations}
            headers={headers}

            rowsPerPageOptions={nbPerPageLabelMedium}
            onClickRow={({ id }) => dispatch(push(`/station/hydrometry/${id}`))}
            defaultSort={initialSort}
            onSort={onTableSort}
        />
    )
}

TablePanel.propTypes = {
    stations: PropTypes.arrayOf(PropTypes.shape({})),
}

const CartoPanel = ({
    stations = [],
}) => {
    const filteredStations = stations.map(s => ({ ...s, code: s.code.value, name: s.name?.value ?? s.name })).filter(s => s && (hasLocalisationStation(s) || s.townCode))

    return filteredStations.length > 20000 ? (
        <MessageCard >{i18n.tooMuchDataToShow}</MessageCard>
    ) : (
        <Card>
            <CardTitle
                title={(
                    <>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{i18n.hydrometricStations}</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'normal' }}>&nbsp;{`(${stations.length} ${getI18nTitleData(i18n.element, i18n.elements, stations)})`}</span>
                    </>
                )}
            />
            <CardContent>
                <CartographyPanel
                    layers={['STATIONS_POINTS']}
                    componentType={STATION_TYPE_NAME.hydrometry}
                    stationsPoints={filteredStations}
                    stationsPanelTitle={i18n.stations}
                    heightToSubstract={450}
                />
            </CardContent>
        </Card>
    )
}

CartoPanel.propTypes = {
    stations: PropTypes.arrayOf(PropTypes.shape({})),
}

const HydrometersDashboardApp = () => {
    const dispatch = useDispatch()

    const {
        hydrometricStations,
        hydrometryCampaigns,
        hydrometryCampaignsProgress,
        citiesIndex,
        globalResearch,
        userBookmarks,
        contacts,
        filterResults,
        contributorLinks,
        contributorsIndex,
        hydrometry,
        accountUser,
        accountHabilitations,
        networks,
        networkLinks,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrometryCampaigns: store.HydrometryReducer.hydrometryCampaigns,
        hydrometryCampaignsProgress: store.HydrometryReducer.hydrometryCampaignsProgress,
        citiesIndex: store.CityReducer.citiesIndex,
        globalResearch: store.HomeReducer.globalResearch,
        userBookmarks: store.UserReducer.userBookmarks,
        contacts: store.ContactReducer.contacts,
        filterResults: store.StationReducer.filterResults,
        contributorLinks: store.StationReducer.contributorLinks,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        hydrometry: store.AdministrationReducer.hydrometry,
        accountUser: store.AccountReducer.accountUser,
        accountHabilitations: store.AccountReducer.accountHabilitations,
        networks: store.NetworkReducer.networks,
        networkLinks: store.HydrometryReducer.networkLinks,
    }), shallowEqual)

    const [filter, setFilter] = useState(() => {
        const {
            filter: filterCode = -1,
            searchValue = '',
        } = hydrometry || {}
        return {
            filter: filterCode,
            searchValue: globalResearch || searchValue,
        }
    })

    const {
        value: isAddStationPopupOpen,
        setTrue: openAddStationPopup,
        setValue: setAddStationPopupOpen,
    } = useBoolean(false)

    const {
        value: isExportOpen,
        setTrue: onOpenExport,
        setFalse: onCloseExport,
    } = useBoolean(false)

    const referentType = useApplicationSetting('contributorTypeAdministrator', value => parseInt(value) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR)
    const operatorType = useApplicationSetting('contributorTypeOperator', value => parseInt(value) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR)

    const [referents, operators] = useMemo(() => ([
        contributorLinks.filter(l => l.contributorType === referentType),
        contributorLinks.filter(l => l.contributorType === operatorType),
    ]), [contributorLinks, operatorType, referentType])

    useEffect(() => {
        if (!componentHasHabilitations(H_HYDRO_DASHBOARD)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
    }, [])

    const {
        isLoaded,
        progress,
    } = useProgressDispatch(() => compact([
        dispatch(HydrometryAction.fetchHydrometricStations()),
        dispatch(StationAction.fetchAllContributors(STATION_TYPE_CONSTANT.hydrometry)),
        dispatch(ContributorAction.fetchContributors()),
        dispatch(CityAction.fetchCities()),
        !networks.length && dispatch(NetworkAction.fetchNetworks()),
        !networkLinks.length && dispatch(HydrometryAction.fetchHydrometryNetworkLinks()),
    ]))

    const {
        isLoaded: isCampaignsLoaded,
        progress: campaignsProgress,
    } = useProgressDispatch(() => {
        if (isLoaded) {
            return [
                dispatch(CampaignAction.fetchHydrometryCampaigns()),
                dispatch(CampaignAction.fetchHydrometryCampaignsProgress()),
            ]
        }
        return []
    }, [isLoaded])

    useTitle(() => [{
        title: i18n.hydrometry,
        href: 'hydrometry',
    }, {
        title: i18n.dashboard,
        href: 'hydrometry',
    }], [])

    const { bookmarkIds: campaignBookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.hydrometry)

    const campaigns = useMemo(() => {
        const currentCampaigns = hydrometryCampaigns.reduce((acc, c) => {
            const { nbStation = 0, nbStationValidated = 0 } = hydrometryCampaignsProgress.find(cp => cp.id === c.id) || {}

            if (nbStation !== 0 && nbStationValidated < nbStation && c.statut === 2) {
                return [
                    ...acc,
                    c,
                ]
            } return acc
        }, [])

        return getCampaignsWithBookmarks(hydrometryCampaigns, currentCampaigns, campaignBookmarkIds).map(campaign => {
            const {
                nbStation,
                progress: campaignProgress = 0,
                progressTotal = 0,
            } = hydrometryCampaignsProgress.find(p => p.id === campaign.id) || {}

            const progressValue = getCampaignProgress(campaignProgress, progressTotal)
            return {
                id: campaign.id,
                qualificationTableColor: {
                    color: getColorCampaign(false, progressValue, 2),
                },
                nullValue: getCampaignIcon(campaign.campaignType, 'small'),
                name: getCampaignNameCell(campaign.name, campaignBookmarkIds.includes(campaign.id)),
                nbStations: nbStation,
                startDate: campaign.beginningApplication,
                progression: <TableProgressBar nb={campaignProgress} nbTotal={progressTotal} progress={progressValue} labelSing={i18n.analyseOn} labelPlur={i18n.analysisOn} />,
                referent: getLabel(contacts, campaign.contactCode, 'mnemonique'),
                contactCode: campaign.contactCode,
            }
        })
    }, [contacts, hydrometryCampaigns, hydrometryCampaignsProgress, campaignBookmarkIds])

    const now = moment().valueOf()

    const hydrometricStationsFormatted = useMemo(() => orderBy(hydrometricStations, 'name').map(station => {
        const filterValuesFound = filterResults.find(fd => fd.id === station.id) || {}
        const filterValues = keys(filterValuesFound).reduce((acc, key) => {
            const value = filterValuesFound[key]
            if (key !== 'id') {
                return { ...acc, [key]: { value: value !== 'null' ? value : '' } }
            } return acc
        }, {})

        const referentLinks = referents.filter(link => link.idStation === station.id && (link.endDate >= now || !link.endDate)) || []
        const operatorLink = operators.find(link => link.idStation === station.id && (link.endDate >= now || !link.endDate)) || {}

        return {
            ...station,

            code: { value: station.code, cellType: 'string' },
            nullValue: getBookmarks(station.code, getUserBookmarksByStationType(userBookmarks, 'hydrometry', station.code)),
            nullValue2: hasValue(station.jobExecutionId) && <i className='material-icons little'>wifi</i>,
            city: getObjectLabel(citiesIndex[station.townCode], 'labelWithCode'),
            name: station.name || '',
            creationDate: { value: getDate(station.creationDate), sortValue: station.creationDate },
            closeDate: { value: getDate(station.closeDate), sortValue: station.closeDate },
            stationType: station.stationType ? getStationType(parseInt(station.stationType)).libelle : '',
            administrator: uniq(compact(referentLinks).map(l => contributorsIndex[l.idContributor]?.name)).join(', '),
            referentId: referentLinks[0]?.idContributor,
            operatorId: operatorLink.idContributor,
            headers: STATION_HEADERS,
            ...filterValues,
        }
    }), [citiesIndex, contributorsIndex, filterResults, hydrometricStations, operators, referents, userBookmarks])

    const containsSearchValue = (station, search) => some(STATION_HEADERS, head => normalizeString(station[head]?.value || station[head]).includes(search))

    const filteredStations = useMemo(() => {
        const {
            searchValue,
            filter: filterCode = -1,
            filterResults: resultFilter = [],
            network,
            referentIds = [],
            operatorIds = [],
        } = filter

        const ids = resultFilter.map(p => p.id)
        const search = normalizeString(searchValue)
        const filterSearch = search ? hydrometricStationsFormatted.filter(s => containsSearchValue(s, search)) : hydrometricStationsFormatted
        const filterStation = filterCode !== -1 ? filterSearch.filter(p => ids.includes(p.id)) : filterSearch
        const filterNetwork = hasValue(network) ? filterStation.filter(p => networkLinks.filter(n => n.idStation === p.id).some(n => n.idNetwork === network && (!n.endDate || n.endDate >= now))) : filterStation
        const filterReferent = referentIds.length === 0 ?
            filterNetwork :
            filterNetwork.filter(s => referents.filter(l => referentIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        const filterOperator = operatorIds.length === 0 ?
            filterReferent :
            filterReferent.filter(s => operators.filter(l => operatorIds.includes(l.idContributor) && (l.endDate >= now || !l.endDate)).some(l => l.idStation === s.id))
        return filterOperator.map(o => ({
            ...o,
            colorLine: o.closeDate?.value ? 'grey' : 'white',
        }))
    }, [filter, hydrometricStationsFormatted, networkLinks, operators, referents])

    useActions(() => {
        const exportAction = {
            exportList: [{
                onClick: onOpenExport,
                label: i18n.excel,
            }],
        }
        if (accountUser.isAdmin === '1' || accountUser.metadata === '1') {
            return {
                ...exportAction,
                new: openAddStationPopup,
            }
        }
        return exportAction
    }, [filteredStations])

    const networksHabilitation = accountHabilitations.filter(({ habilitation }) => habilitation.match('^HYDRO_NETWORK_*')).map(({ habilitation }) => parseInt(habilitation.substr(14)))
    const contributorsHabilitation = accountHabilitations.filter(({ habilitation }) => habilitation.match('^HYDRO_CONTRIB_*')).map(({ habilitation }) => parseInt(habilitation.substr(14)))
    const networksContributors = networks.filter(({ contributorCode }) => contributorsHabilitation.includes(contributorCode))
    const networksId = [...networkLinks.filter(n => !n.endDate || n.endDate >= now).map(({ idNetwork }) => idNetwork), ...networksHabilitation]
    const networklistFound = uniq(networksId).map(id => networks.find(n => n.id === id)).filter(n => !!n)
    const piezoNetworks = uniqBy([...networksContributors, ...networklistFound], 'id')

    return (
        <>
            {!isLoaded && <ProgressCard progress={progress} withMessage />}
            {isLoaded && (
                <Grid container spacing={1}>
                    <Grid size={9}>
                        {isLoaded && isCampaignsLoaded ? (
                            <CampaignPortletPanel
                                campaigns={campaigns}
                                stationType={STATION_TYPE_NAME.hydrometry}
                                headers={CAMPAIGN_HEADERS}
                            />
                        ) : <ProgressCard progress={!isLoaded ? 0 : campaignsProgress} withMessage />}
                    </Grid>
                    <Grid size={3}>
                        <StationStatisticPanel stationType={STATION_TYPE_NAME.hydrometry} round />
                    </Grid>
                    <Grid size={12}>
                        <SimpleTabList
                            defaultTab={STATION_LIST}
                            tabs={[
                                {
                                    constant: STATION_LIST,
                                    label: i18n.table,
                                    icon: 'list',
                                },
                                {
                                    constant: MAP,
                                    label: i18n.map,
                                    icon: 'map',
                                },
                            ]}
                            headerComponent={(
                                <StationFilterFields
                                    defaultFilter={filter}
                                    onValidate={setFilter}
                                    stationType={STATION_TYPE_NAME.hydrometry}
                                    contributorLinks={contributorLinks}
                                    stations={hydrometricStations}
                                    networklist={piezoNetworks}
                                />
                            )}
                            noPadding
                        >
                            {tab => (
                                <>
                                    {tab === STATION_LIST && <TablePanel stations={filteredStations} />}
                                    {tab === MAP && <CartoPanel stations={filteredStations} />}
                                </>
                            )}
                        </SimpleTabList>
                    </Grid>
                </Grid>
            )}
            <AddHydroStepper
                isOpen={isAddStationPopupOpen}
                setIsOpen={setAddStationPopupOpen}
            />
            <ExportModal
                isOpen={isExportOpen}
                onClose={onCloseExport}
                filteredHydrometers={filteredStations}
            />
        </>
    )
}

export default HydrometersDashboardApp
